/**
 * @Page WST02
 * @Description [WST02] 완성차 재고관리 - 현황관리 - 재고현황
 * @Table WRTWQTY 실재고 수량
 * 기준일자 ±7일 날짜 컬럼 + 기준일자 빨간색 하이라이트
 * 수량 클릭 시 해당 날짜/품번의 차수별 상세 모달 표출
 **/
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Modal, Box } from "@material-ui/core";
import Ribbon from '@/components/organisms/Ribbon';
import GridRibbon from "@/components/organisms/GridRibbon";
import ContentXearch from '@/components/organisms/ContentXearch';
import Content from '@/components/organisms/Content';
import Grid from '@/components/grid/agGrid';
import CustomAlert from "@/components/molecules/CustomAlert";
import AlertDialog from '@/components/material-UI/AlertDialog';
import Loading from "src/components/atoms/CustomLoading2";
import * as Common from '@/utils/Common';
import CommonUtil from '@/utils/CommonUtil';

const SERVER_URL = process.env.REACT_APP_BACKEND_SERVER;
const Utils = new CommonUtil();

const wst02NoColumn = {
    headerName: "No",
    field: "no",
    cellClass: ["nocheckbox", "alignR"],
    valueGetter: node => String(node.node.rowIndex + 1),
    minWidth: 60,
    maxWidth: 60,
    pinned: "left",
};

// ─── 날짜 유틸 ────────────────────────────────────────────────────────────────
/** YYYY-MM-DD → Date */
function parseDate(str) {
    if (!str) return new Date();
    const s = str.replace(/-/g, "");
    return new Date(
        parseInt(s.substring(0, 4)),
        parseInt(s.substring(4, 6)) - 1,
        parseInt(s.substring(6, 8))
    );
}

/** Date → YYYY-MM-DD */
function formatDate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

/** Date → YYYYMMDD */
function formatDateNoHyphen(d) {
    return formatDate(d).replace(/-/g, "");
}

const getDeletedStateLabel = (row = {}) => {
    const labels = [];
    if (row.wareexistyn === "N") labels.push("창고 삭제");
    else if (row.wareyn === "N") labels.push("창고 미사용");
    if (row.recd && row.resiexistyn === "N") labels.push("상주원 삭제");
    else if (row.resiyn === "N" && row.wareexistyn !== "N") labels.push("상주원 연계 삭제");
    else if (row.recd && row.resimasteryn === "N") labels.push("상주원 미사용");
    if (row.grptexistyn === "N") labels.push("품목그룹 삭제");
    else if (row.resigrptyn === "N") labels.push("품목그룹 연계 삭제");
    else if (row.gryn === "N") labels.push("품목그룹 미사용");
    if (row.ptyn === "N") labels.push("품번 삭제");
    if (labels.length === 0 && row.activeyn === "N") labels.push("연계 삭제");
    return labels.length > 0 ? labels.join(", ") : "사용";
};

const deletedCellStyle = {
    color: "#c62828",
    fontWeight: 600,
    textDecoration: "line-through",
    textDecorationThickness: "1px",
    textDecorationColor: "#ff8585",
};

const unusedCellStyle = {
    color: "#1565c0",
    fontWeight: 600,
    textDecoration: "line-through",
    textDecorationThickness: "1px",
    textDecorationColor: "#64b5f6",
};

const getRowStateStyle = (row = {}) => {
    const deleted = row.wareexistyn === "N"
        || row.resiexistyn === "N"
        || row.resiyn === "N"
        || row.grptexistyn === "N"
        || row.resigrptyn === "N"
        || row.ptyn === "N";
    if (deleted) return deletedCellStyle;

    const unused = row.wareyn === "N"
        || row.resimasteryn === "N"
        || row.gryn === "N";
    if (unused) return unusedCellStyle;

    return row.activeyn === "N" ? deletedCellStyle : undefined;
};

// ─── WareOrdr 팝업과 동일한 MUI Modal 스타일 ───────────────────────────────
const ordrModalBoxStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 700,
    height: 560,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    paddingTop: '10px',
    paddingBottom: '10px',
};

// ─── 차수별 상세 팝업 (RST01 WareOrdr 팝업과 동일한 구조) ────────────────────
const OrdrDetailModal = ({ open, onClose, ptno, stdt, detailList }) => {

    const displayPtno = ptno && ptno.length > 5
        ? ptno.substring(0, 5) + "-" + ptno.substring(5)
        : ptno;
    const displayStdt = stdt
        ? stdt.replace(/(\d{4})(\d{2})(\d{2})/, "$1-$2-$3")
        : "";

    const tableStyle = {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "13px",
        fontFamily: "PretendardLight, NanumGothic, sans-serif",
    };
    const thStyle = {
        background: "#f0f4fa",
        border: "1px solid #d0d7e5",
        padding: "7px 10px",
        textAlign: "center",
        fontWeight: "bold",
        color: "#334",
        fontSize: "13px",
    };
    const tdStyle = {
        border: "1px solid #e0e5ef",
        padding: "7px 10px",
        textAlign: "center",
        color: "#444",
        fontSize: "13px",
    };
    const tdRightStyle = { ...tdStyle, textAlign: "right" };
    const tdLeftStyle  = { ...tdStyle, textAlign: "left" };

    return (
        <Modal
            open={open}
            onClose={onClose}
        >
            <Box sx={ordrModalBoxStyle}>
                {/* 닫기 버튼 - WareOrdr 팝업과 동일 */}
                <button
                    onClick={onClose}
                    style={{
                        border: "none",
                        float: "right",
                        paddingRight: "0px",
                        background: "transparent",
                        cursor: "pointer",
                    }}
                >
                    <img src="/images/button/close_button.png" alt="close" />
                </button>

                {/* Ribbon - WareOrdr 팝업과 동일한 구조 */}
                <Ribbon
                    newTabUnused={true}
                    onInfoManual={false}
                >
                    <b style={{ color: "#cd5c5c", fontFamily: "PretendardLight, NanumGothic, sans-serif" }}>
                        차수별 재고 상세&nbsp;|&nbsp;{displayPtno}&nbsp;|&nbsp;{displayStdt}
                    </b>
                </Ribbon>

                {/* 테이블 영역 - WareOrdr의 grid_type01과 동일한 래핑 */}
                <div className="grid_type01" style={{ marginTop: "10px" }}>
                    <div className="grid_wrap01">
                        <div className="grid_cont_box" style={{ height: "380px", width: "100%", overflowY: "auto" }}>
                            {detailList && detailList.length > 0 ? (
                                <table style={tableStyle}>
                                    <thead>
                                        <tr>
                                            <th style={thStyle}>NO</th>
                                            <th style={thStyle}>차수</th>
                                            <th style={thStyle}>상주원</th>
                                            <th style={thStyle}>기준정보 상태</th>
                                            <th style={thStyle}>수량</th>
                                            <th style={thStyle}>비고</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detailList.map((row, idx) => (
                                            <tr
                                                key={idx}
                                                style={{ background: idx % 2 === 0 ? "#fff" : "#f8faff" }}
                                            >
                                                <td style={tdStyle}>{idx + 1}</td>
                                                <td style={tdStyle}>{row.ordrdisp || row.ordr || ""}</td>
                                                <td style={tdLeftStyle}>{row.recd ? `${row.recd}${row.renm ? ` (${row.renm})` : ""}` : ""}</td>
                                                <td style={{ ...tdStyle, ...(getRowStateStyle(row) || {}) }}>
                                                    {getDeletedStateLabel(row)}
                                                </td>
                                                <td style={tdRightStyle}>
                                                    {row.q0 !== undefined && row.q0 !== null
                                                        ? Number(row.q0).toLocaleString()
                                                        : ""}
                                                </td>
                                                <td style={tdLeftStyle}>{row.etc || ""}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div style={{
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    height: "100%", color: "#999", fontSize: "13px",
                                    fontFamily: "PretendardLight, NanumGothic, sans-serif",
                                }}>
                                    조회된 데이터가 없습니다.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Box>
        </Modal>
    );
};

// ─── WST02 메인 컴포넌트 ───────────────────────────────────────────────────────
function WST02() {
    const token = Common.getToken();
    const tVnt1 = Common.getToken("vand");
    const tComp = Common.getToken("comp");

    const grdWST02Main = useRef();    // 메인 그리드 (날짜별 재고현황)

    // ─── 상태 ────────────────────────────────────────────────────────────────
    const [idata, setData] = useState({
        kVnt1: tVnt1,
        kComp: tComp,
        kWrcd: "",          // 창고코드
        kOrdr: "1",         // 차수 (최초 조회는 1차수)
        kGrtp: "",          // 품번그룹
        kRecd: "",          // 상주원
        kPtno: "",          // 품번
        kStdt: Common.getToday(), // 기준일자
        status: "ALL",       // 기준정보 상태 단일 필터
        resiUsed: "Y",
        resiDeleted: "N",
        resiUnused: "N",
        groupUsed: "Y",
        groupDeleted: "N",
        groupUnused: "N",
        partUsed: "Y",
        partDeleted: "N",

        // 콤보 목록
        wrcdList: [],       // 창고코드 목록
        ordrList: [],       // 차수 목록
        grptList: [],       // 품번그룹 목록
        recdList: [],       // 상주원 목록
    });

    const [message, setMessage] = useState({ openAlert: false, severity: "success", msg: "" });
    const { openAlert, severity, msg } = message;
    const [loading, setLoading] = useState(false);

    const [dlgMessage, setDlgMessage] = useState({ openDlg: false, dlgTrans: "", dlgMsg: "" });
    const { openDlg, dlgTrans, dlgMsg } = dlgMessage;

    let [searchApi, setSearchApi] = useState(false);
    const onClearApi = useCallback(async () => {
        setSearchApi(false);
    }, []);

    const [panded, setPanded] = useState(true);
    const [style, setStyle] = useState({ height: "calc(100vh - 65px - 35px - 50px - 21px - 56px - 8px)" });

    // ─── 차수 상세 모달 상태 ──────────────────────────────────────────────────
    const [modalOpen, setModalOpen] = useState(false);
    const [modalPtno, setModalPtno] = useState("");
    const [modalStdt, setModalStdt] = useState("");
    const [modalDetailList, setModalDetailList] = useState([]);

    // ─── Mount: 콤보 데이터 조회 ──────────────────────────────────────────────
    useEffect(() => {
        onSearchWrcdList();
        onSearchOrdrList();
        onSearchGrptList();
        onSearchRecdList();
    }, []);

    // ─── 기준일자 또는 차수 변경 시 자동 재조회 ────────────────────────────────
    useEffect(() => {
        if (!idata.kStdt) return;
        const stdt = idata.kStdt.replace(/-/g, "");
        const ptno = idata.kPtno ? idata.kPtno.replace(/-/g, "") : "";
        const para = `{"kVnt1":"${idata.kVnt1}","kComp":"${idata.kComp}","wrcd":"${idata.kWrcd}","ordr":"","grtp":"${idata.kGrtp}","recd":"${idata.kRecd}","ptno":"${ptno}","stdt":"${stdt}","status":"${idata.status}","resiUsed":"Y","resiDeleted":"${idata.resiDeleted}","resiUnused":"${idata.resiUnused}","groupUsed":"Y","groupDeleted":"${idata.groupDeleted}","groupUnused":"${idata.groupUnused}","partUsed":"Y","partDeleted":"${idata.partDeleted}"}`;
        const param = encodeURI(para);
        setSearchApi(SERVER_URL + "/wst02/qty/search?query=" + param);
    }, [idata.kStdt, idata.kOrdr]);

    // 창고코드 목록 조회
    const onSearchWrcdList = async () => {
        const para = `{"kVnt1":"${tVnt1}","kWrcd":"","wareyn":"*"}`;
        const param = encodeURI(para);
        const query = SERVER_URL + "/rst01/ware/search?query=" + param;
        await fetch(query, { headers: { Authorization: token } })
            .then(resp => resp.json())
            .then(data => {
                if (data.success && data.data) {
                    const list = [{ comCode: "", text: "전체" },
                        ...data.data.map(w => ({
                            comCode: w.wrcd,
                            text: `${w.wrcd} - ${w.wrnm}${w.wareyn === "N" ? " [미사용]" : ""}`,
                        }))];
                    setData(prev => ({ ...prev, wrcdList: list }));
                }
            })
            .catch(() => {});
    };

    // 선택한 창고의 차수 목록 조회 (창고 미선택 시 업체 전체 차수)
    const onSearchOrdrList = async (wrcd = "") => {
        const para = `{"wrcd":"${wrcd}"}`;
        const param = encodeURI(para);
        const query = SERVER_URL + "/rst01/ordr/search?query=" + param;
        await fetch(query, { headers: { Authorization: token } })
            .then(resp => resp.json())
            .then(data => {
                if (data.success && data.data) {
                    const uniqueOrders = [...new Map(data.data.map(o => [o.ordr, o])).values()]
                        .sort((a, b) => String(a.ordr).localeCompare(String(b.ordr), undefined, { numeric: true }));
                    const list = [{ comCode: "", text: "전체" },
                        ...uniqueOrders.map(o => ({
                            comCode: o.ordr,
                            text: wrcd && o.etc ? `${o.ordr}차수 - ${o.etc}` : `${o.ordr}차수`,
                        }))];
                    setData(prev => ({ ...prev, ordrList: list }));
                }
            })
            .catch(() => {});
    };

    // 품번그룹 목록 조회
    const onSearchGrptList = async () => {
        const para = `{"kVnt1":"${tVnt1}","kGrtp":"","kGryn":"*"}`;
        const param = encodeURI(para);
        const query = SERVER_URL + "/rst03/wagr/search?query=" + param;
        await fetch(query, { headers: { Authorization: token } })
            .then(resp => resp.json())
            .then(data => {
                if (data.success && data.data) {
                    const list = [{ comCode: "", text: "전체" },
                        ...data.data.map(g => ({
                            comCode: g.grtp,
                            text: `${g.grtp} - ${g.grnm}${g.gryn === "N" ? " [미사용]" : ""}`,
                        }))];
                    setData(prev => ({ ...prev, grptList: list }));
                }
            })
            .catch(() => {});
    };

    // 상주원 목록 조회
    const onSearchRecdList = async () => {
        const para = `{"kVnt1":"${tVnt1}","recd":"","useyn":"*"}`;
        const param = encodeURI(para);
        const query = SERVER_URL + "/rst02/resi/search?query=" + param;
        await fetch(query, { headers: { Authorization: token } })
            .then(resp => resp.json())
            .then(data => {
                if (data.success && data.data) {
                    const list = [{ comCode: "", text: "전체" },
                        ...data.data.map(r => ({
                            comCode: r.recd,
                            text: `${r.recd} - ${r.renm}${r.useyn === "N" ? " [미사용]" : ""}`,
                        }))];
                    setData(prev => ({ ...prev, recdList: list }));
                }
            })
            .catch(() => {});
    };

    // ─── Control 변경 ─────────────────────────────────────────────────────────
    const onChange = async (e) => {
        const { name, value } = e.target;
        if (name === "kWrcd") {
            setData(prev => ({ ...prev, kWrcd: value, kOrdr: "1" }));
            await onSearchOrdrList(value);
            return;
        }
        setData(prev => ({ ...prev, [name]: value }));
    };

    const onSearchKeyDown = (e) => {
        if (e.key === "Enter" && ["INPUT", "SELECT"].includes(e.target.tagName)) {
            e.preventDefault();
            onSearch();
        }
    };

    // ─── 조회 ─────────────────────────────────────────────────────────────────
    const onSearch = async () => {
        const stdt = idata.kStdt ? idata.kStdt.replace(/-/g, "") : "";
        // WST01과 동일하게 품번의 표시용 하이픈을 제거하고 조회한다.
        const ptno = idata.kPtno ? idata.kPtno.replace(/-/g, "") : "";
        const para = `{"kVnt1":"${idata.kVnt1}","kComp":"${idata.kComp}","wrcd":"${idata.kWrcd}","ordr":"","grtp":"${idata.kGrtp}","recd":"${idata.kRecd}","ptno":"${ptno}","stdt":"${stdt}","status":"${idata.status}","resiUsed":"Y","resiDeleted":"${idata.resiDeleted}","resiUnused":"${idata.resiUnused}","groupUsed":"Y","groupDeleted":"${idata.groupDeleted}","groupUnused":"${idata.groupUnused}","partUsed":"Y","partDeleted":"${idata.partDeleted}"}`;
        const param = encodeURI(para);
        const query = SERVER_URL + "/wst02/qty/search?query=" + param;
        setSearchApi(query);
    };

    // ─── Excel 출력 (REPD0 형식) ───────────────────────────────────────
    const onExcel = async () => {
        const stdt = idata.kStdt ? idata.kStdt.replace(/-/g, "") : "";
        const ptno = idata.kPtno ? idata.kPtno.replace(/-/g, "") : "";
        const para = `{"kVnt1":"${idata.kVnt1}","kComp":"${idata.kComp}","wrcd":"${idata.kWrcd}","ordr":"","grtp":"${idata.kGrtp}","recd":"${idata.kRecd}","ptno":"${ptno}","stdt":"${stdt}","status":"${idata.status}","resiUsed":"Y","resiDeleted":"${idata.resiDeleted}","resiUnused":"${idata.resiUnused}","groupUsed":"Y","groupDeleted":"${idata.groupDeleted}","groupUnused":"${idata.groupUnused}","partUsed":"Y","partDeleted":"${idata.partDeleted}"}`;
        const query = SERVER_URL + "/wst02/excel?query=" + encodeURIComponent(para);

        setLoading(true);
        try {
            const resp = await fetch(query, { headers: { Authorization: token } });
            if (!resp.ok) throw new Error("Excel download failed");
            const blob = await resp.blob();
            const href = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = href;
            // 서버 JSP Excel View는 HTML 기반의 구형 XLS 형식이므로 확장자를 XLS로 맞춘다.
            link.setAttribute("download", "WST02.xls");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(href);
            setMessage({ openAlert: true, severity: "success", msg: "엑셀파일이 생성되었습니다." });
        } catch (e) {
            setMessage({ openAlert: true, severity: "error", msg: "엑셀파일 생성에 실패했습니다." });
        } finally {
            setLoading(false);
        }
    };

    // ─── 날짜 클릭 시 차수별 상세 조회 (stdt: YYYYMMDD 직접 전달) ────────────
    const onClickQty = async (ptno, stdtNo, wrcd, grtp) => {
        if (!ptno || !stdtNo) return;
        const para = `{"kVnt1":"${idata.kVnt1}","kComp":"${idata.kComp}","wrcd":"${wrcd}","grtp":"${grtp}","ordr":"","ptno":"${ptno}","stdt":"${stdtNo}"}`;
        const param = encodeURI(para);
        const query = SERVER_URL + "/wst02/qty/detail?query=" + param;

        setModalPtno(ptno);
        setModalStdt(stdtNo);
        setModalDetailList([]);
        setModalOpen(true);

        try {
            const resp = await fetch(query, { headers: { Authorization: token } });
            const data = await resp.json();
            if (data.success && data.data) {
                setModalDetailList(data.data);
            }
        } catch (e) {
            setModalDetailList([]);
        }
    };

    // ─── AlertDialog ──────────────────────────────────────────────────────────
    const AlertDialogResult = async (e) => {
        setDlgMessage({ openDlg: false, dlgTrans: "", dlgMsg: "" });
    };

    // ─── 검색조건 ─────────────────────────────────────────────────────────────
    const optionsX = {
        0: [
            {
                formatter: "input", name: "kStdt", labelName: "기준일자", type: "date",
                defaultValue: idata.kStdt, align: "left", labelType: "none",
            },
            {
                formatter: "select", name: "kWrcd", labelName: "창고코드", type: "text",
                defaultValue: idata.kWrcd, codeMode: false, selectAll: "",
                codes: idata.wrcdList, comms: null,
            },
            {
                formatter: "select", name: "kRecd", labelName: "상주원", type: "text",
                defaultValue: idata.kRecd, codeMode: false, selectAll: "",
                codes: idata.recdList, comms: null,
            },
            {
                formatter: "check", name: "resiStatusFlags", labelName: "", labelType: "none", align: "left", compact: true,
                options: [
                    { name: "resiDeleted", labelName: "삭제포함", defaultValue: idata.resiDeleted },
                    { name: "resiUnused", labelName: "미사용포함", defaultValue: idata.resiUnused },
                ],
            },
             { formatter: "blank", align: "right" },
             { formatter: "blank", align: "right" },
            // { formatter: "blank", align: "right" },
            // { formatter: "blank", align: "right" },
        ],
        1: [
            {
                formatter: "select", name: "kGrtp", labelName: "품번그룹", type: "text",
                defaultValue: idata.kGrtp, codeMode: false, selectAll: "",
                codes: idata.grptList, comms: null,
            },
            {
                formatter: "check", name: "groupStatusFlags", labelName: "", labelType: "none", align: "left", compact: true,
                options: [
                    { name: "groupDeleted", labelName: "삭제포함", defaultValue: idata.groupDeleted },
                    { name: "groupUnused", labelName: "미사용포함", defaultValue: idata.groupUnused },
                ],
            },
            // { formatter: "blank", align: "right" },
            {
                formatter: "input", name: "kPtno", labelName: "품번", type: "text",
                defaultValue: idata.kPtno, readOnly: false, align: "left", labelType: "none", compact: true, compactLabel: true,
            },
            {
                formatter: "check", name: "partStatusFlags", labelName: "", labelType: "none", align: "left", compact: true,
                options: [
                    { name: "partDeleted", labelName: "삭제포함", defaultValue: idata.partDeleted },
                ],
            },
            { formatter: "blank", align: "right" },
            { formatter: "blank", align: "right" },
            { formatter: "blank", align: "right" },
            
            
        ],
    };

    // ─── 날짜 인덱스 목록 (-7 ~ +7) ─────────────────────────────────────────
    // 백엔드 응답 필드: d_7~d_1, d0~d7 (날짜 YYYYMMDD), q_7~q_1, q0~q7 (수량)
    // 백엔드 DTO 필드명: 과거(음수)는 p접두어 사용 (dp7, qp7 등)
    // 기준일 이후(0 이상)는 기존과 동일 (d0, q0 / d1, q1 등)
    const DATE_KEYS = [
        { dKey: "dp7", qKey: "qp7", offset: -7 },
        { dKey: "dp6", qKey: "qp6", offset: -6 },
        { dKey: "dp5", qKey: "qp5", offset: -5 },
        { dKey: "dp4", qKey: "qp4", offset: -4 },
        { dKey: "dp3", qKey: "qp3", offset: -3 },
        { dKey: "dp2", qKey: "qp2", offset: -2 },
        { dKey: "dp1", qKey: "qp1", offset: -1 },
        { dKey: "d0",  qKey: "q0",  offset:  0 },
        { dKey: "d1",  qKey: "q1",  offset:  1 },
        { dKey: "d2",  qKey: "q2",  offset:  2 },
        { dKey: "d3",  qKey: "q3",  offset:  3 },
        { dKey: "d4",  qKey: "q4",  offset:  4 },
        { dKey: "d5",  qKey: "q5",  offset:  5 },
        { dKey: "d6",  qKey: "q6",  offset:  6 },
        { dKey: "d7",  qKey: "q7",  offset:  7 },
    ];

    // ─── Grid 컬럼 정의 ────────────────────────────────────────────────────────
    const columnDefs = useMemo(() => {
        // 기준일자(YYYYMMDD) - d0 컬럼이 기준일
        const baseStr = idata.kStdt ? idata.kStdt.replace(/-/g, "") : Common.getToday().replace(/-/g, "");

        // 고정 컬럼
        const fixedCols = [
            {
                headerName: "창고코드", field: "wrcd",
                sortable: true, filter: true, flex: 1, minWidth: 120, maxWidth: 170,
                editable: false, cellClass: ["nocheckbox", "alignL"],
                pinned: "left",
                valueFormatter: params => {
                    const code = params.value ?? "";
                    const name = params.data?.wrnm ?? "";
                    const historyName = String(name).startsWith("[삭제 이력]") ? "" : name;
                    const text = historyName ? `${code} (${historyName})` : code;
                    if (params.data?.wareexistyn === "N") return `${text} (삭제)`;
                    if (params.data?.wareyn === "N") return `${text} (미사용)`;
                    return text;
                },
                cellStyle: params => {
                    if (params.data?.wareexistyn === "N") return deletedCellStyle;
                    if (params.data?.wareyn === "N") return unusedCellStyle;
                    return undefined;
                },
            },
            
            {
                headerName: "차수", field: "ordrdisp",
                sortable: true, filter: true, flex: 1, minWidth: 70, maxWidth: 150,
                editable: false, cellClass: ["nocheckbox", "alignL"],
                pinned: "left",
            },
            {
                headerName: "상주원", field: "renm",
                sortable: true, filter: true, flex: 1, minWidth: 80, maxWidth: 100,
                editable: false, cellClass: ["nocheckbox", "alignC"],
                pinned: "left",
                valueFormatter: params => {
                    const code = params.data?.recd ?? "";
                    const name = params.value ?? "";
                    const text = code ? (name ? `${name}` : code) : name;
                    if (code && params.data?.resiexistyn === "N") return `${text} (삭제)`;
                    if (params.data?.resiyn === "N") return `${text} (연계 삭제)`;
                    if (code && params.data?.resimasteryn === "N") return `${text} (미사용)`;
                    return text;
                },
                cellStyle: params => {
                    if (params.data?.resiexistyn === "N" || params.data?.resiyn === "N") {
                        return deletedCellStyle;
                    }
                    if (params.data?.resimasteryn === "N") return unusedCellStyle;
                    return undefined;
                },
            },
            {
                headerName: "품번그룹", field: "grtp",
                sortable: true, filter: true, flex: 2, minWidth: 220, maxWidth: 300,
                editable: false, cellClass: ["nocheckbox", "alignL"],
                pinned: "left",
                valueFormatter: params => {
                    const code = params.value ?? "";
                    const name = params.data?.grnm ?? params.data?.grptnm ?? "";
                    const text = name ? `${code} (${name})` : code;
                    if (params.data?.grptexistyn === "N") return `${text} (삭제)`;
                    if (params.data?.resigrptyn === "N") return `${text} (연계 삭제)`;
                    if (params.data?.gryn === "N") return `${text} (미사용)`;
                    return text;
                },
                cellStyle: params => {
                    if (params.data?.grptexistyn === "N" || params.data?.resigrptyn === "N") {
                        return deletedCellStyle;
                    }
                    if (params.data?.gryn === "N") return unusedCellStyle;
                    return undefined;
                },
            },
            // {
            //     headerName: "기준정보 상태", field: "activeyn",
            //     sortable: true, filter: true, flex: 1, minWidth: 140, maxWidth: 240,
            //     editable: false, cellClass: ["nocheckbox", "alignC"],
            //     valueFormatter: params => getDeletedStateLabel(params.data),
            //     cellStyle: params => params.value === "N"
            //         ? { color: "#d32f2f", fontWeight: "bold" }
            //         : undefined,
            // },
              {
                headerName: "공장코드", field: "plnt",
                sortable: true, filter: true, flex: 1, minWidth: 70, maxWidth: 80,
                editable: false, cellClass: ["nocheckbox", "alignC"],
                pinned: "left",
                valueGetter: params => params.data?.plnt ?? params.data?.wrcplnt ?? params.data?.wdcplnt ?? params.data?.factory ?? "",
            },
            {
                headerName: "품번", field: "ptno",
                sortable: true, filter: true, flex: 2, minWidth: 80, maxWidth: 90,
                editable: false, cellClass: ["nocheckbox", "alignL"],
                pinned: "left",
                valueGetter: Utils.partnoGetter,
                valueFormatter: params => params.data?.ptyn === "N" ? `${params.value ?? ""} (삭제)` : (params.value ?? ""),
                cellStyle: params => params.data?.ptyn === "N"
                    ? deletedCellStyle
                    : undefined,
            },
          
        ];

        // 날짜 컬럼: d_7~d_1, d0~d7 → q_7~q_1, q0~q7
        // 기준일자(idata.kStdt) + offset 으로 헤더 날짜를 직접 계산
        // → kStdt 변경 즉시 useMemo 재실행되어 헤더가 실시간 갱신됨
        const dateCols = DATE_KEYS.map(({ dKey, qKey, offset }) => {
            const isBase = offset === 0;

            // 헤더 라벨: idata.kStdt + offset 으로 직접 MM/DD 계산
            const base = parseDate(idata.kStdt || Common.getToday());
            const targetDay = new Date(base);
            targetDay.setDate(base.getDate() + offset);
            const mm = String(targetDay.getMonth() + 1).padStart(2, "0");
            const dd = String(targetDay.getDate()).padStart(2, "0");
            const headerLabel = `${mm}/${dd}`;

            return {
                headerName: headerLabel,
                field: qKey,
                sortable: true,
                filter: true,
                flex: 1,
                minWidth: 68,
                editable: false,
                cellClass: ["nocheckbox", "alignR"],
                headerClass: isBase ? "wst02-base-date-header" : "",
                cellStyle: (params) => {
                    const val = params.value;
                    const baseStyle = isBase
                        ? { color: "#d32f2f", fontWeight: "bold", background: "#fff5f5" }
                        : {};
                    if (val !== null && val !== undefined && val !== "") {
                        return {
                            ...baseStyle,
                            cursor: "pointer",
                            textDecoration: "underline",
                            textUnderlinePosition: "under",
                            color: isBase ? "#d32f2f" : "#1976d2",
                            fontWeight: "bold",
                        };
                    }
                    return isBase ? { ...baseStyle } : null;
                },
                onCellClicked: (params) => {
                    const val = params.value;
                    if (val !== null && val !== undefined && val !== "") {
                        const rawPtno = params.data?.ptno || "";
                        const dateVal = params.data?.[dKey] || "";  // YYYYMMDD
                        const wrcd = params.data?.wrcd || "";
                        const grtp = params.data?.grtp || "";
                        onClickQty(rawPtno, dateVal, wrcd, grtp);
                    }
                },
                valueFormatter: (params) => {
                    if (params.value === null || params.value === undefined || params.value === "") return "";
                    return Number(params.value).toLocaleString();
                },
            };
        });

        // WST02에서는 No를 고정 컬럼보다 앞에 명시적으로 둡니다.
        return [wst02NoColumn, ...fixedCols, ...dateCols];
    }, [idata.kStdt]);

    const onClick = async () => {
        setPanded(!panded);
        if (panded === true) {
            setStyle({ height: "calc(100vh - 65px - 35px - 50px - 21px - 8px)" });
        } else {
            setStyle({ height: "calc(100vh - 65px - 35px - 50px - 21px - 56px - 8px)" });
        }
    };

    return (
        <>
            {/* 차수별 상세 모달 */}
            <OrdrDetailModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                ptno={modalPtno}
                stdt={modalStdt}
                detailList={modalDetailList}
            />

            <Loading loading={loading} />

            {/* Dialog */}
            <AlertDialog open={openDlg} trans={dlgTrans} message={dlgMsg} onClose={AlertDialogResult} />

            {/* Alert */}
            <CustomAlert
                open={openAlert} severity={severity} duration={5000} message={msg}
                onClose={() => setMessage({ openAlert: false, severity: "", msg: "" })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            />

            {/* Ribbon */}
            <Ribbon
                onSearch={onSearch} onSearchName={null}
                onExcel={onExcel} onExcelName={null}
            >
                {"완성차 재고관리 > 현황관리 > "}
                <b style={{ color: "#cd5c5c", fontFamily: "PretendardMedium, NanumGothic, sans-serif" }}>
                    재고현황
                </b>
            </Ribbon>

            {/* 검색조건 */}
            <div onKeyDown={onSearchKeyDown}>
                <ContentXearch optionsX={optionsX} onChange={onChange} onClick={onClick} />
            </div>

            {/* Content */}
            <Content style={style}>
                {/* 기준일자 헤더 스타일 인라인 삽입 */}
                <style>{`
                    .wst02-base-date-header {
                        color: #d32f2f !important;
                        font-weight: bold !important;
                        background: #fff0f0 !important;
                    }
                    .wst02-base-date-header .ag-header-cell-text {
                        color: #d32f2f !important;
                    }
                `}</style>

                <div className="grid_type01">
                    <div className="grid_wrap01">
                        <GridRibbon onSearch={onSearch} onSearchName={null} />
                        <div className="grid_cont_box" style={{ height: "calc(100%)" }}>
                            <Grid
                                ref={grdWST02Main}
                                gridId={"WST02"}
                                columnDefs={columnDefs}
                                pagination={true}
                                paginationCsv={true}
                                paginationPageSize={"1000"}
                                rowMultiSelectWithClick={false}
                                suppressRowClickSelection={true}
                                rowSelection={"single"}
                                enableRangeSelection={false}
                                enableCellTextSelection={true}
                                isFirst={false}
                                displayNo={false}
                                searchApi={searchApi}
                                onClearApi={onClearApi}
                                onCellValueChangedEvent={async () => {}}
                            />
                        </div>
                    </div>
                </div>
            </Content>
        </>
    );
}

export default WST02;
